import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  DoorSliding, 
  ArrowForward,
  Refresh,
  Settings,
  Save
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { getAuthToken } from '../../../utils/cookieUtils';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Types
interface Facility {
  id: number;
  name: string;
}

interface DockDoor {
  dock_uuid: string;
  dock_name: string;
  order_number: number;
  trailer_uuid?: string;
  zone_name?: string;
  current_status?: string;
  assigned_at?: string;
  mdt?: string;
  detention?: string;
  dock_timer?: string;
  direction?: string;
  paused?: boolean; // Paused status - true means paused, false/null/undefined means not paused
  // Enhanced trailer and shipment data
  trailer?: {
    uuid: string;
    trailer_number?: string;
    carrier_name?: string;
    shipment_id?: string;
    shipment_reference_number?: string;
  };
}

// Widget configuration
interface DockWidgetConfig {
  showOnlyOccupied: boolean;
  showOnlyNeedingAttention: boolean;
  occupiedThresholdHours: number;
  showAvailableCount: boolean;
  sortBy: 'dock_name' | 'status' | 'zone_name' | 'mdt' | 'detention' | 'dock_timer';
  sortOrder: 'asc' | 'desc';
}

interface DockDashboardWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
}

// Color configuration (copied from DockManagement.tsx)
const colorConfig = {
  mdt: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  detention: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  dockTimer: {
    warning: '#f97316',
    critical: '#ef4444',
    good: '#22c55e',
    warningThreshold: 30,
    criticalThreshold: 60,
    textColor: '#ffffff'
  },
  card: {
    default: '#ffffff',
    oneWarning: '#fef3c7',
    twoWarnings: '#fde68a',
    threeWarnings: '#fcd34d',
    oneCritical: '#fee2e2',
    twoCriticals: '#fecaca',
    threeCriticals: '#fca5a5',
    mixed: '#f3f4f6',
    textColor: '#1f2937'
  }
};

// Helper function to extract minutes from timer string (copied from DockManagement.tsx)
const getMinutesFromTimer = (timeString: string): number => {
  if (!timeString) return 0;
  
  // Handle negative times (starting with -)
  const isNegative = timeString.startsWith('-');
  const cleanTimeString = isNegative ? timeString.substring(1) : timeString;
  
  const parts = cleanTimeString.split(':');
  if (parts.length >= 2) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    // If seconds are included, handle them too
    const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) / 60 : 0;
    
    return (hours * 60 + minutes + seconds) * (isNegative ? -1 : 1);
  }
  
  return 0;
};

// Helper functions for timer calculations (copied from DockManagement)
const calculateCountdownTime = (targetDateString: string, timezone = 'America/New_York'): string => {
  try {
    const targetDate = new Date(targetDateString);
    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      // Past the deadline
      const absDiffMs = Math.abs(diffMs);
      const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `-${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    } else {
      // Time remaining
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }
  } catch (error) {
    return "00:00:00";
  }
};

const calculateElapsedTime = (startDateString: string): string => {
  try {
    const startDate = new Date(startDateString);
    const now = new Date();
    const diffMs = now.getTime() - startDate.getTime();
    
    if (diffMs <= 0) return "00:00:00";
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  } catch (error) {
    return "00:00:00";
  }
};

// Helper function to get time status (copied from DockManagement.tsx)
const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention' | 'dockTimer'): 'red' | 'green' | 'orange' => {
  // For MDT and detention, if there's no data or it's 00:00:00, treat as green (N/A state)
  if ((timerType === 'mdt' || timerType === 'detention') && (!timeString || timeString === '00:00:00')) {
    return 'green';
  }
  
  const minutes = getMinutesFromTimer(timeString);
  const config = colorConfig[timerType];
  
  // For MDT and detention: check thresholds for both remaining time and overdue time
  // For dock timer: positive = elapsed time (higher = worse)
  if (timerType === 'mdt' || timerType === 'detention') {
    if (minutes > 0) {
      // Time remaining - check if we're approaching deadlines
      if (minutes <= config.criticalThreshold) {
        return 'red'; // Critical - very little time remaining
      } else if (minutes <= config.warningThreshold) {
        return 'orange'; // Warning - getting close to deadline
      }
      return 'green'; // Good - plenty of time remaining
    } else {
      // Overdue - always critical
      return 'red';
    }
  } else {
    // Dock timer logic (elapsed time, higher = worse)
    if (minutes >= config.criticalThreshold) {
      return 'red';
    } else if (minutes >= config.warningThreshold) {
      return 'orange';
    }
    return 'green';
  }
};

// Helper function to determine if a dock is in use (copied from DockManagement.tsx)
const isDockInUse = (door: DockDoor): boolean => {
  return !!(door.trailer_uuid || door.trailer?.trailer_number);
};

// Helper function to get display status (updated to handle paused docks)
const getDisplayStatus = (door: DockDoor): string => {
  // Show paused status first
  if (door.paused) {
    return 'PAUSED';
  }
  
  if (!isDockInUse(door)) {
    return 'AVAILABLE';
  }
  
  const status = door.current_status?.toLowerCase();
  const direction = door.direction?.toLowerCase();
  
  // Inbound and LOADED = TRAILER READY
  if (direction === 'inbound' && status === 'loaded') {
    return 'TRAILER READY';
  }
  
  // Outbound and EMPTY = TRAILER READY  
  if (direction === 'outbound' && status === 'empty') {
    return 'TRAILER READY';
  }
  
  // Everything else is passthrough
  if (door.current_status) {
    return door.current_status.toUpperCase();
  }
  
  return 'OCCUPIED';
};

// Helper function to get card background color (updated to handle paused docks)
const getCardBackgroundColor = (door: DockDoor) => {
  // If dock is paused, always show gray (overrides all other status colors)
  if (door.paused) {
    return '#D1D5DB'; // Gray-300
  }
  
  // Only apply timer-based coloring for docks that are in use
  if (!isDockInUse(door)) {
    return colorConfig.card.default;
  }

  const statuses = [
    getTimeStatus(door.mdt || '00:00:00', 'mdt'),
    getTimeStatus(door.detention || '00:00:00', 'detention'),
    getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer')
  ];
  
  const warningCount = statuses.filter(status => status === 'orange').length;
  const criticalCount = statuses.filter(status => status === 'red').length;

  if (criticalCount === 3) return colorConfig.card.threeCriticals;
  if (criticalCount === 2) return colorConfig.card.twoCriticals;
  if (criticalCount === 1) return colorConfig.card.oneCritical;
  if (warningCount === 3) return colorConfig.card.threeWarnings;
  if (warningCount === 2) return colorConfig.card.twoWarnings;
  if (warningCount === 1) return colorConfig.card.oneWarning;
  if (warningCount > 0 || criticalCount > 0) return colorConfig.card.mixed;
  return colorConfig.card.default;
};

const DockDashboardWidget: React.FC<DockDashboardWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  // Widget configuration with localStorage persistence
  const [config, setConfig] = useState<DockWidgetConfig>(() => {
    const saved = localStorage.getItem('dockWidgetConfig');
    return saved ? JSON.parse(saved) : {
      showOnlyOccupied: true,
      showOnlyNeedingAttention: true,
      occupiedThresholdHours: 2,
      showAvailableCount: true,
      sortBy: 'dock_name',
      sortOrder: 'asc'
    };
  });

  // Save config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dockWidgetConfig', JSON.stringify(config));
  }, [config]);

  // Process raw data from consolidated query
  const dockDoors: DockDoor[] = React.useMemo(() => {
    if (!dashboardData?.warehouse?.warehouseZones) return [];

    const allDocks: DockDoor[] = [];

    // Get facility timezone from configuration
    let facilityTimezone = 'America/New_York';
    if (dashboardData.facility?.configuration) {
      const facConfig = typeof dashboardData.facility.configuration === 'string'
        ? JSON.parse(dashboardData.facility.configuration)
        : dashboardData.facility.configuration;
      facilityTimezone = facConfig.timezone || 'America/New_York';
    }

    dashboardData.warehouse.warehouseZones.forEach((zone: any) => {
      zone.docks.forEach((dock: any) => {
        const isInUse = !!(dock.trailer_uuid || dock.shipment?.id || dock.driver?.id);

        let dockTimer = "00:00:00";
        if (isInUse) {
          const lastMovedAt = dock.trailer?.active_lineage?.last_moved_at;
          if (lastMovedAt) {
            dockTimer = calculateElapsedTime(lastMovedAt);
          }
        }

        let mdt = "";
        let detention = "";
        if (isInUse && dock.shipment?.metadata) {
          try {
            const metadata = typeof dock.shipment.metadata === 'string'
              ? JSON.parse(dock.shipment.metadata)
              : dock.shipment.metadata;
            if (metadata.appointment_must_depart_by_date) {
              mdt = calculateCountdownTime(metadata.appointment_must_depart_by_date, facilityTimezone);
            }
            if (metadata.detention_time) {
              detention = calculateCountdownTime(metadata.detention_time, facilityTimezone);
            }
          } catch (err) {
            console.error('Error parsing shipment metadata:', err);
          }
        }

        let paused = false;
        if (dock.configuration) {
          try {
            const dockConfig = typeof dock.configuration === 'string'
              ? JSON.parse(dock.configuration)
              : dock.configuration;
            paused = dockConfig.paused || false;
          } catch (err) {
            console.error('Error parsing dock configuration:', err);
          }
        }

        allDocks.push({
          dock_uuid: dock.dockUuid,
          dock_name: dock.dockName,
          order_number: dock.order_number ? parseInt(dock.order_number, 10) : 0,
          trailer_uuid: dock.trailer_uuid,
          zone_name: zone.zoneName,
          current_status: dock.trailer?.status?.name || (isInUse ? 'in-use' : 'available'),
          direction: dock.shipment?.direction,
          mdt,
          detention,
          dock_timer: dockTimer,
          assigned_at: dock.shipment?.driver_assignment?.created_at,
          paused,
          trailer: dock.trailer_uuid ? {
            uuid: dock.trailer_uuid,
            trailer_number: dock.trailer_name,
            carrier_name: dock.carrier_name,
            shipment_id: dock.shipment?.id,
            shipment_reference_number: dock.shipment?.referenceNumber
          } : undefined
        });
      });
    });

    return allDocks;
  }, [dashboardData?.warehouse, dashboardData?.facility]);

  const loading = !dashboardData && !error;

  // Navigate to full dock management page
  const handleViewAll = () => {
    navigate('/dock');
  };



  // Open door details modal - navigate to dock management page with door context
  const handleDoorClick = (door: DockDoor) => {
    navigate(`/dock?door=${door.dock_uuid}`);
  };



  // Save widget configuration to facility
  const handleSaveConfig = async () => {
    if (!selectedFacility) return;

    try {
      setSaving(true);

      // Get current facility configuration
      const facilityQuery = `
        query GetFacility {
          facility(id: "${selectedFacility.id}") {
            id
            name
            configuration
          }
        }
      `;

      const authToken = getAuthToken();

      const facilityResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ query: facilityQuery })
      });

      const facilityResult = await facilityResponse.json();
      if (facilityResult.errors) {
        throw new Error(facilityResult.errors[0].message);
      }

      // Parse existing configuration
      const existingConfig = facilityResult.data.facility?.configuration ? 
        (typeof facilityResult.data.facility.configuration === 'string' ? 
          JSON.parse(facilityResult.data.facility.configuration) : 
          facilityResult.data.facility.configuration) : {};

      // Update widgets configuration
      const updatedConfig = {
        ...existingConfig,
        widgets: {
          ...existingConfig.widgets,
          dockDashboard: config
        }
      };

      // Save updated configuration
      const updateMutation = `
        mutation UpdateFacility($id: ID!, $input: FacilityUpdateInput!) {
          update_facility(id: $id, input: $input) {
            id
            name
            configuration
          }
        }
      `;

      const updateResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateMutation,
          variables: {
            id: selectedFacility.id,
            input: {
              name: facilityResult.data.facility.name,
              configuration: JSON.stringify(updatedConfig)
            }
          }
        })
      });

      const updateResult = await updateResponse.json();
      if (updateResult.errors) {
        throw new Error(updateResult.errors[0].message);
      }

      console.log('Widget configuration saved successfully');
    } catch (err) {
      console.error('Failed to save widget configuration:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Filter doors based on configuration
  const occupiedDoors = dockDoors.filter(door => door.trailer_uuid);
  const availableDoors = dockDoors.filter(door => !door.trailer_uuid);
  
  // Get doors that need attention based on thresholds (exclude paused docks)
  const doorsNeedingAttention = occupiedDoors.filter(door => {
    // Exclude paused docks from needing attention
    if (door.paused) return false;
    
    if (!door.assigned_at) return true; // No assignment time = needs attention
    
    const assignedTime = new Date(door.assigned_at);
    const hoursOccupied = (Date.now() - assignedTime.getTime()) / (1000 * 60 * 60);
    return hoursOccupied >= config.occupiedThresholdHours;
  });

  // Determine what doors to display based on config
  let doorsToShow: DockDoor[] = [];
  if (config.showOnlyNeedingAttention) {
    doorsToShow = doorsNeedingAttention;
  } else if (config.showOnlyOccupied) {
    doorsToShow = occupiedDoors;
  } else {
    doorsToShow = dockDoors;
  }

  // Apply sorting
  const sortedDoors = [...doorsToShow].sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (config.sortBy) {
      case 'dock_name':
        aValue = a.dock_name;
        bValue = b.dock_name;
        break;
      case 'status':
        aValue = getDisplayStatus(a);
        bValue = getDisplayStatus(b);
        break;
      case 'zone_name':
        aValue = a.zone_name || '';
        bValue = b.zone_name || '';
        break;
      case 'mdt':
        aValue = getMinutesFromTimer(a.mdt || '00:00:00');
        bValue = getMinutesFromTimer(b.mdt || '00:00:00');
        break;
      case 'detention':
        aValue = getMinutesFromTimer(a.detention || '00:00:00');
        bValue = getMinutesFromTimer(b.detention || '00:00:00');
        break;
      case 'dock_timer':
        aValue = getMinutesFromTimer(a.dock_timer || '00:00:00');
        bValue = getMinutesFromTimer(b.dock_timer || '00:00:00');
        break;
      default:
        aValue = a.dock_name;
        bValue = b.dock_name;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return config.sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return config.sortOrder === 'asc' ? (aValue - bValue) : (bValue - aValue);
    }
  });
  
  const displayDoors = compactMode ? sortedDoors.slice(0, 8) : sortedDoors.slice(0, 12);

  return (
    <Paper sx={{ 
      p: 3, 
      height: showConfig ? 'auto' : (compactMode ? 320 : 400),
      minHeight: compactMode ? 320 : 400,
      display: 'flex',
      flexDirection: 'column',
      overflow: showConfig ? 'visible' : 'hidden'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DoorSliding sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            {config.showOnlyNeedingAttention ? 'Doors Needing Attention' : 'Dock Status'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Save Configuration">
            <IconButton 
              onClick={handleSaveConfig} 
              disabled={saving || !selectedFacility}
              size="small"
              color="primary"
            >
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configure">
            <IconButton 
              onClick={() => setShowConfig(!showConfig)} 
              size="small"
              color={showConfig ? 'primary' : 'default'}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading && !isRefreshing} size="small">
              <Refresh className={isRefreshing ? 'animate-spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Configuration Panel */}
      <Collapse in={showConfig}>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Widget Configuration</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showOnlyNeedingAttention}
                onChange={(e) => setConfig(prev => ({ ...prev, showOnlyNeedingAttention: e.target.checked }))}
                size="small"
              />
            }
            label="Show only doors needing attention"
            sx={{ display: 'block', mb: 1 }}
          />
          
          {!config.showOnlyNeedingAttention && (
            <FormControlLabel
              control={
                <Switch
                  checked={config.showOnlyOccupied}
                  onChange={(e) => setConfig(prev => ({ ...prev, showOnlyOccupied: e.target.checked }))}
                  size="small"
                />
              }
              label="Show only occupied doors"
              sx={{ display: 'block', mb: 1 }}
            />
          )}
          
          <TextField
            label="Attention threshold (hours)"
            type="number"
            size="small"
            value={config.occupiedThresholdHours}
            onChange={(e) => setConfig(prev => ({ ...prev, occupiedThresholdHours: parseInt(e.target.value) || 2 }))}
            sx={{ width: 150, mb: 1 }}
            inputProps={{ min: 1, max: 24 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={config.showAvailableCount}
                onChange={(e) => setConfig(prev => ({ ...prev, showAvailableCount: e.target.checked }))}
                size="small"
              />
            }
            label="Show available count"
            sx={{ display: 'block', mb: 2 }}
          />

          {/* Sort Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={config.sortBy}
                label="Sort By"
                onChange={(e) => setConfig(prev => ({ ...prev, sortBy: e.target.value as any }))}
              >
                <MenuItem value="dock_name">Door Name</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="zone_name">Zone</MenuItem>
                <MenuItem value="mdt">MDT</MenuItem>
                <MenuItem value="detention">Detention</MenuItem>
                <MenuItem value="dock_timer">Dock Timer</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={config.sortOrder}
                label="Order"
                onChange={(e) => setConfig(prev => ({ ...prev, sortOrder: e.target.value as any }))}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>

      {!selectedFacility ? (
        <Typography variant="body2" color="warning.main">
          Select a facility to view dock doors
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : dockDoors.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dock doors found
        </Typography>
      ) : (
        <>
          {/* Status Overview */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {config.showOnlyNeedingAttention && (
              <Chip 
                label={`${doorsNeedingAttention.length} Need Attention`}
                color="error"
                size="small"
                variant="outlined"
              />
            )}
            {(config.showOnlyOccupied || !config.showOnlyNeedingAttention) && (
              <Chip 
                label={`${occupiedDoors.length} Occupied`}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
            {config.showAvailableCount && (
              <Chip 
                label={`${availableDoors.length} Available`}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {sortedDoors.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {config.showOnlyNeedingAttention 
                ? `No doors need attention (${config.occupiedThresholdHours}h+ threshold)`
                : config.showOnlyOccupied 
                  ? "All dock doors are available" 
                  : "No dock doors found"
              }
            </Typography>
          ) : (
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              {/* Grid of Dock Door Boxes */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: compactMode ? 'repeat(auto-fit, minmax(80px, 1fr))' : 'repeat(auto-fit, minmax(100px, 1fr))', 
                gap: 0.5, 
                flexGrow: 1, 
                overflow: 'auto',
                maxHeight: compactMode ? '240px' : '300px'
              }}>
                {displayDoors.map((door) => (
                  <Tooltip 
                    key={door.dock_uuid}
                    title={`${door.zone_name ? `${door.zone_name} - ` : ''}Door ${door.dock_name} - ${getDisplayStatus(door)}`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        aspectRatio: '1',
                        cursor: 'pointer',
                        border: '2px solid #9CA3AF',
                        backgroundColor: getCardBackgroundColor(door),
                        color: door.paused ? '#374151' : '#000000',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleDoorClick(door)}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '10px' : '11px',
                          textAlign: 'center'
                        }}
                      >
                        {door.dock_name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: compactMode ? '9px' : '10px',
                          mt: 0.3,
                          textAlign: 'center',
                          px: 0.5,
                          lineHeight: 1.2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {getDisplayStatus(door)}
                      </Typography>
                      {/* Show trailer number if available */}
                      {isDockInUse(door) && door.trailer?.trailer_number && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: compactMode ? '7px' : '8px',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1,
                            color: 'rgba(0,0,0,0.7)',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {door.trailer.trailer_number}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {sortedDoors.length > displayDoors.length && (
              <Typography variant="caption" color="text.secondary">
                +{sortedDoors.length - displayDoors.length} more {config.showOnlyNeedingAttention ? 'needing attention' : 'doors'}
              </Typography>
            )}
            <Button 
              size="small" 
              endIcon={<ArrowForward />}
              onClick={handleViewAll}
              sx={{ ml: 'auto' }}
            >
              View All Doors
            </Button>
          </Box>
        </>
      )}


    </Paper>
  );
};

export default DockDashboardWidget; 